// @eslint-disable
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GridRowParams } from '@material-ui/data-grid';
import { observer } from 'mobx-react-lite';
import _ from 'lodash';
import { toJS } from 'mobx';
import { ListItem, Typography } from '@material-ui/core';
import ActionsMenu from '../../components/other/ActionsMenu';
import { gridColumn } from '../../utils/gridUtils';
import { drawerStore } from '../../state/drawerStore';
import { FormInputTypes } from '../../utils/constants';
import { Api } from '../../api';
import { usePaginatedProvider } from '../../state/paginatedProviderStore';
import { VMPaginatedGrid } from '../../components/main/VMPaginatedGrid';
import { USER_DETAILS_TABS, userDetailsTabParameters } from '../../utils/routing';
import { ReactComponent as NotificationsIcon } from '../../images/Activities/notifications.svg';
import { ReactComponent as CommentsIcon } from '../../images/Activities/comments.svg';
import TextLink from '../../components/other/TextLink';
import { TaskStatusColors } from '../../types/TaskStatusPill';
import { Task } from '../../types/Charger';
import { TaskLog } from '../../types/TaskLog';

const getChargerOptions = (chargerProvider) =>
  chargerProvider.data?.map((charger) => {
    const label = `${charger.manufacturer} / ${charger.model}`;
    return {
      topRow: label,
      bottomRow: charger.ocppId,
      optionName: charger.id,
      label,
    };
  });

const getLocationOptions = (locationProvider) =>
  locationProvider.data?.map((location) => {
    const label = `${location.address}, ${location.city}`;
    return {
      topRow: label,
      bottomRow: location.name,
      optionName: location.id,
    };
  });

const getUserOptions = (userProvider) =>
  userProvider?.data?.users.map((user) => {
    const label = `${user.name} ${user.familyName}`;
    return {
      topRow: label,
      bottomRow: user.id,
      optionName: user.id,
    };
  });

const ActivitiesTable = observer(
  ({
    createdByUser = null,
    locationId = null,
    chargePointId = null,
  }: {
    createdByUser?: string | null;
    locationId?: string | null;
    chargePointId?: string | null;
  }) => {
    const activitiesProvider = usePaginatedProvider(undefined, undefined, false);
    const chargerProvider = usePaginatedProvider(undefined, undefined, false);
    const userProvider = usePaginatedProvider(undefined, undefined, false);
    const locationProvider = usePaginatedProvider(undefined, undefined, false);
    const taskRemindersProvider = usePaginatedProvider(undefined, undefined, false);
    const taskLogsProvider = usePaginatedProvider(undefined, undefined, false);
    const [sortableFields, setSortableFields] = useState([]);
    const reminderCounts = _.countBy(taskRemindersProvider?.data || [], 'taskId');
    const { pathname } = useLocation();
    const pathSegments = pathname.split('/');

    const [currentLocationId, setCurrentLocationId] = useState(null);
    const [currentChargePointId, setCurrentChargePointId] = useState(null);

    useEffect(() => {
      if (pathSegments.includes('locations')) {
        setCurrentLocationId(pathSegments[2]);
        setCurrentChargePointId(null);
      } else if (pathSegments.includes('charge-points')) {
        setCurrentChargePointId(pathSegments[3]);
        setCurrentLocationId(null);
      }
    }, [pathname]);

    const chargePointInputs = [
      {
        name: 'chargePoint',
        label: 'Charge Point',
        type: currentChargePointId
          ? FormInputTypes.disabledTextField
          : FormInputTypes.multilineSelect,
        value: currentChargePointId,
        options: !currentChargePointId ? getChargerOptions(chargerProvider) : undefined,
      },
    ];

    const locationInputs = [
      {
        name: 'location',
        label: 'Location',
        type: currentLocationId ? FormInputTypes.disabledTextField : FormInputTypes.multilineSelect,
        value: currentLocationId,
        options: !currentLocationId ? getLocationOptions(locationProvider) : undefined,
      },
    ];

    const activityCreateInputs = [
      {
        name: 'assignee',
        label: 'Assignee',
        type: FormInputTypes.multilineSelect,
        options: getUserOptions(userProvider),
      },
      { name: 'description', label: 'Description', type: FormInputTypes.textField },
    ];

    const editActivityInputs = [
      {
        name: 'status',
        label: 'Status',
        type: FormInputTypes.select,
        options: ['OPENED', 'IN PROGRESS', 'CLOSED'],
      },
      {
        name: 'assignedTo',
        label: 'Assignee',
        type: FormInputTypes.multilineSelect,
        options: getUserOptions(userProvider),
      },
      {
        name: 'reminder',
        label: 'Reminder',
        type: FormInputTypes.multiDateTimePicker,
      },
    ];

    const addCommentInputs = [
      { name: 'comment', label: 'Write a Comment', type: FormInputTypes.textField },
    ];

    const selectHolder = (selectedInputs) => {
      const combinedInputs = [...selectedInputs, ...activityCreateInputs];
      drawerStore.setInputs(combinedInputs);
      drawerStore.setLabel('Create Task');
    };

    const activitySelectHolderInputs = [
      {
        name: 'chargePoint',
        label: 'Charge Point',
        type: FormInputTypes.button,
        inputChangeCallback: () => selectHolder(chargePointInputs),
      },
      {},
      {
        name: 'location',
        label: 'Location',
        type: FormInputTypes.button,
        inputChangeCallback: () => selectHolder(locationInputs),
      },
    ];

    const onClickSaveEdit = (taskId: string, currentStatus: string, currentAssignee: string) => {
      const data = toJS(drawerStore.values);
      const { assignedTo, status, ...rest } = data;
      const updatedData = { ...rest, assignee: assignedTo };

      const taskLogData = { ...updatedData, status, id: taskId };
      const taskReminderData = { times: data.reminder, taskId };
      drawerStore.reset();

      const userBelongsToTask = userProvider.data.users.find((user) => user.id === assignedTo);
      const userFullName = `${userBelongsToTask?.name || ''} ${
        userBelongsToTask?.familyName || ''
      }`;

      const createTaskLog = (type: TaskLog, description: string) => {
        const taskLog = {
          taskId,
          type,
          description,
        };
        Api.charge.createTaskLog(taskLog).then(taskRemindersProvider.reloadResource);
      };

      if (assignedTo !== currentAssignee) {
        createTaskLog(TaskLog.ASSIGNEE_CHANGE, 'Assigned to ' + userFullName);
      }

      if (status !== currentStatus) {
        createTaskLog(TaskLog.STATUS_CHANGE, 'Status changed to ' + status);
      }

      Api.charge.editTask(taskLogData).then(taskRemindersProvider.reloadResource);
      Api.charge.updateTaskReminders(taskReminderData).then(activitiesProvider.reloadResource);
    };

    const handleEditButton = (task: Task) => {
      const remindersData = Object.keys(drawerStore.values)
        .filter((key) => key.startsWith('reminder'))
        .map((key) => drawerStore.values[key]);

      const initialValues = {
        status: task.status,
        assignedTo: task.assignedTo.userId,
        reminder: remindersData,
      };

      drawerStore.setInputs(editActivityInputs);
      drawerStore.setLabel('Edit Task');
      drawerStore.setValues(initialValues);
      drawerStore.setButtonLabel('Save Changes');
      drawerStore.setCallback(() => onClickSaveEdit(task.id, task.status, task.assignedTo.userId));
    };

    const generateTaskReminderInputs = (task: Task) => {
      const taskReminders = taskRemindersProvider.data
        .filter((reminder) => reminder.taskId === task.id)
        .map((reminder, index) => {
          const reminderName = `reminder${index + 1}`;
          const reminderLabel = index === 0 ? 'Reminder' : '';

          const reminderDate = new Date(reminder.time);
          const day = String(reminderDate.getDate()).padStart(2, '0');
          const month = String(reminderDate.getMonth() + 1).padStart(2, '0');
          const year = reminderDate.getFullYear();
          const hours = String(reminderDate.getHours()).padStart(2, '0');
          const minutes = String(reminderDate.getMinutes()).padStart(2, '0');
          const formattedDateTime = `${day}.${month}.${year} at ${hours}:${minutes}`;

          return {
            name: reminderName,
            label: reminderLabel,
            value: formattedDateTime,
            isEditable: true,
            color: '#707070',
            type: FormInputTypes.horizontalRow,
          };
        });

      return taskReminders;
    };

    const generateTaskLogInputs = (task: Task) => {
      const taskLogs = taskLogsProvider.data
        .filter((log) => log.taskId === task.id)
        .map((log, index) => {
          const userBelongsToTask = userProvider.data.users.find((user) => user.id === log.userId);
          const userFullName = `${userBelongsToTask?.name || ''} ${
            userBelongsToTask?.familyName || ''
          }`;

          return {
            name: `TaskLog${index + 1}`,
            label: '',
            userName: userFullName || '',
            logType: log.type,
            logDate: new Date(log.createdAt).toLocaleString(),
            logText: log.description,
            type: FormInputTypes.taskLogField,
          };
        });

      taskLogsProvider.reloadResource();
      return taskLogs;
    };

    const activityDetailsInputs = (task: Task) => [
      {
        name: 'activityMessage',
        label: 'Task Message',
        color: '#707070',
        type: FormInputTypes.textView,
      },
      {
        name: 'description',
        label: '',
        type: FormInputTypes.longTextView,
      },
      {},
      {
        name: 'createdBy',
        label: 'Created By',
        isEditable: false,
        color: '#707070',
        type: FormInputTypes.horizontalRow,
      },
      {
        name: 'createdAt',
        label: 'Created At',
        isEditable: false,
        color: '#707070',
        type: FormInputTypes.horizontalRow,
      },
      {
        name: 'status',
        label: 'Status',
        isEditable: true,
        color: '#707070',
        inputChangeCallback: () => handleEditButton(task),
        type: FormInputTypes.horizontalRow,
      },
      {
        name: 'assignedTo',
        label: 'Assigned To',
        isEditable: true,
        color: '#707070',
        inputChangeCallback: () => handleEditButton(task),
        type: FormInputTypes.horizontalRow,
      },
      ...generateTaskReminderInputs(task).map((reminder) => ({
        ...reminder,
        inputChangeCallback: () => handleEditButton(task),
      })),
      ...generateTaskLogInputs(task),
    ];

    const onClickConfirmAdd = () => {
      const data = toJS(drawerStore.values);
      Api.charge.createTask(data).then(activitiesProvider.reloadResource);
    };

    const onClickConfirmAddComment = async (task: Task) => {
      const data = toJS(drawerStore.values);
      const commentPayload = {
        taskId: task.id,
        type: TaskLog.COMMENT,
        description: data.comment,
      };

      Api.charge.createTaskLog(commentPayload).then(activitiesProvider.reloadResource);
    };

    const onClickAddComment = (task: Task) => {
      drawerStore.setInputs(addCommentInputs);
      drawerStore.setButtonLabel('Add');
      drawerStore.setCallback(() => onClickConfirmAddComment(task));
    };

    const onClickActivityAdd = () => {
      if (currentChargePointId) {
        drawerStore.openDrawer({
          label: 'Create Task',
          inputs: [...chargePointInputs, ...activityCreateInputs],
          clickCallback: onClickConfirmAdd,
        });
        return;
      }

      if (currentLocationId) {
        drawerStore.openDrawer({
          label: 'Create Task',
          inputs: [...locationInputs, ...activityCreateInputs],
          clickCallback: onClickConfirmAdd,
        });
        return;
      }

      drawerStore.openDrawer({
        label: 'Select Task Type',
        inputs: activitySelectHolderInputs,
        clickCallback: onClickConfirmAdd,
      });
    };

    useEffect(() => {
      const requestFunctionForChargers = (offset, sortBy, sortOrder) =>
        Api.charge.listChargers({ skip: offset, sortBy, sortOrder });

      chargerProvider.setRequest(requestFunctionForChargers);

      chargerProvider.setOnFulfilled((_data, _count, sortable) => {
        if (sortable) setSortableFields(sortable);
      });
      chargerProvider.reloadResource();
    }, [chargerProvider]);

    useEffect(() => {
      const requestFunctionForTaskLogs = () => Api.charge.listAllTaskLogs();

      taskLogsProvider.setRequest(requestFunctionForTaskLogs);

      taskLogsProvider.setOnFulfilled((_data, _count, sortable) => {
        if (sortable) setSortableFields(sortable);
      });
      taskLogsProvider.reloadResource();
    }, [taskLogsProvider]);

    useEffect(() => {
      const requestFunctionForLocations = () => Api.charge.listLocations();

      locationProvider.setRequest(requestFunctionForLocations);

      locationProvider.setOnFulfilled((_data, _count, sortable) => {
        if (sortable) setSortableFields(sortable);
      });
      locationProvider.reloadResource();
    }, [locationProvider]);

    useEffect(() => {
      const requestFunctionForUsers = () => Api.user.listUsers({ isAdmin: true });

      userProvider.setRequest(requestFunctionForUsers);

      userProvider.setOnFulfilled((_data, _count, sortable) => {
        if (sortable) setSortableFields(sortable);
      });
      userProvider.reloadResource();
    }, [userProvider]);

    useEffect(() => {
      let filter = {};
      if (createdByUser) {
        filter = { createdBy: createdByUser };
      }

      if (locationId) {
        filter = { ...filter, locationId };
      }

      if (chargePointId) {
        filter = { ...filter, chargePointId };
      }

      activitiesProvider?.setRequest(() => Api.charge.listAllTasks(filter));

      activitiesProvider?.setOnFulfilled((_data, _count, sortable) => {
        if (sortable) {
          setSortableFields(sortable);
        }
      });

      activitiesProvider?.reloadResource();
    }, [createdByUser]);

    useEffect(() => {
      const fetchReminders = () => Api.charge.listAllTaskReminders();
      taskRemindersProvider.setRequest(fetchReminders);
      taskRemindersProvider.reloadResource();
    }, [taskRemindersProvider]);

    const onClickShowDetails = (task: Task) => {
      const selectedTask = activitiesProvider.data.find((item) => item.id === task.id);
      drawerStore.openDrawer({
        label: 'Task Details',
        inputs: activityDetailsInputs(task),
        clickCallback: () => onClickAddComment(task),
        buttonLabel: 'Add Comment',
        saveWithoutReset: true,
      });
      const createdAt = new Date(selectedTask.createdAt);

      const date = createdAt.toDateString();
      const time = createdAt.toLocaleTimeString();

      const formattedDateTime = `${date} at ${time}`;

      const fullName = `${selectedTask?.assignedTo.user || ''}`;
      const initialValues = {
        description: selectedTask.description,
        createdBy: selectedTask.createdBy?.user,
        createdAt: formattedDateTime,
        status: selectedTask.status,
        assignedTo: fullName,
      };
      drawerStore.setValues(initialValues);
    };

    const operationConfigActions = (params: GridRowParams) => (
      <ActionsMenu>
        <ListItem button onClick={() => onClickShowDetails(params.row as Task)}>
          <Typography style={{ width: '100%' }}>Show Details</Typography>
        </ListItem>
      </ActionsMenu>
    );

    const activityColumns = [
      gridColumn('createdBy', {
        flex: 3,
        sortableFields,
        minWidth: 150,
        renderCell: (params: GridRowParams) => {
          const formattedDateTime = `${new Date(params.row.createdAt).toLocaleDateString(
            'en-GB',
          )} ${new Date(params.row.createdAt).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
          })}`;

          return (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
              <TextLink
                label={params.row.createdBy.user}
                to={userDetailsTabParameters(
                  params.row.createdBy.userId,
                  USER_DETAILS_TABS.OVERVIEW,
                )}
                divStyle={{ marginBottom: '0.375rem' }}
              />
              <span style={{ color: '#697483', fontSize: '0.9rem' }}>@ {formattedDateTime}</span>
            </div>
          );
        },
      }),

      gridColumn('description', {
        flex: 3,
        sortableFields,
        minWidth: 150,
        renderCell: (params: GridRowParams) => <div>{params.row.description}</div>,
      }),
      gridColumn('assignee', {
        flex: 3,
        sortableFields,
        minWidth: 150,
        renderCell: (params: GridRowParams) => <div>{params.row.assignedTo.user}</div>,
      }),
      gridColumn('status', {
        flex: 2,
        sortableFields,
        minWidth: 150,
        renderCell: (params: GridRowParams) => (
          <span style={{ color: TaskStatusColors[params.row.status] || 'black' }}>
            {params.row.status}
          </span>
        ),
      }),

      gridColumn('holder', {
        flex: 3,
        sortableFields,
        minWidth: 150,
        renderCell: (params: GridRowParams) => (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'normal' }}>
            <div style={{ marginBottom: '0.375rem' }}>
              {params.row.locationId === null ? 'OCPP ID' : 'Location Name'}
            </div>
            <div style={{ color: '#697483' }}>{params.row.holder}</div>
          </div>
        ),
      }),

      gridColumn('comments', {
        flex: 3,
        sortableFields,
        minWidth: 150,
        renderCell: (params: GridRowParams) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginRight: '2rem',
              }}
            >
              <NotificationsIcon />
              <div>{reminderCounts[params.row.id] || 0}</div>
            </span>

            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CommentsIcon />
              <div>{params.row.commentsCount}</div>
            </span>
          </div>
        ),
      }),
      gridColumn('', {
        width: 60,
        renderCell: (params: GridRowParams) => operationConfigActions(params),
        align: 'center',
        headerAlign: 'center',
      }),
    ];

    return (
      <VMPaginatedGrid
        onClickAdd={onClickActivityAdd}
        label="Tasks"
        count={activitiesProvider.count}
        rows={activitiesProvider.data}
        columns={activityColumns}
        onPageOffsetChange={activitiesProvider.onPageChange}
        onSortTypeChange={activitiesProvider.setSort}
        rowHeight={60}
      />
    );
  },
);

export default ActivitiesTable;
